import numberal from '@tiger/numeral';
import { useSWR } from '@tiger/swr';
import { Button, Form } from 'antd';
import lscache from 'lscache';
import { FC, useEffect, useMemo } from 'react';
import { useForm, withForm } from 'react-formutil';
import { useNavigate } from 'react-router-dom';
import BottomBox from 'components/BottomBox';
import CurrencyAmount from 'components/CurrencyAmount';
import ErrorBox from 'components/ErrorBox';
import FieldSelect, { FieldSelectDataItemTypes } from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import HashLink from 'components/HashLink';
import Loading from 'components/Loading';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import SwitchContributionWay from 'modules/Deposit/components/SwitchContributionWay';
import TaxLimit from 'modules/Deposit/components/TaxLimit';
import { IRALimits, TaxLimitInfo, TransferAccount } from 'modules/Deposit/type';
import security from 'stores/security';
import { AccountCustomerTypeEnum } from 'types/user';
import API from 'utils/API';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { getCurrencyInfo } from 'utils/util';
import { LocationStateTypes } from '../Record';
import style from './style.scss';

const emptyArr = [];

const Fill: FC = () => {
    const navigate = useNavigate();
    const $formutil = useForm();
    const { $invalid, $getFirstError, $params, $batchDirty, $setValues } = $formutil;
    const transferFromAccountId = $params.from_account_id;
    const taxYear = $params.tax_year;
    const cacheKey = `CACHE_TRANSFER_PARAMS_${security.currentCustomerId}`;

    const currencyList = useMemo(() => {
        const usdInfo = getCurrencyInfo('USD');

        return [
            {
                value: 'USD',
                label: usdInfo.name,
                icon: usdInfo.icon,
                key: usdInfo.name
            }
        ];
    }, []);

    const {
        data = emptyArr,
        error,
        isLoading,
        mutate
    } = useSWR<TransferAccount[]>(
        {
            url: API.TRADE.deposit.getTransferAccounts(),
            params: {
                account_rel_type: 'INDIVIDUAL_IRA'
            }
        },
        {
            dataChain: resp =>
                resp.data?.map((item: TransferAccount) => ({ ...item, title: `${item.title}(${item.brId})` }))
        }
    );

    const { data: taxLimitData = emptyArr, isLoading: taxLimitLoading } = useSWR<IRALimits[]>(
        security.isIRAAccount &&
            transferFromAccountId && {
                url: API.TRADE.deposit.getIRALimits(),
                params: {
                    accountId: security.currentAccountId
                }
            },
        {
            dataChain: resp => resp.data
        }
    );

    const taxInfo = useMemo(() => {
        const taxItem = taxLimitData.find(item => item.taxYear === taxYear);

        return {
            done: taxItem?.successDepositAmount || 0,
            rest: taxItem?.remainingLimit || 0
        };
    }, [taxYear, taxLimitData]);

    const { data: cashInfo } = useSWR<{
        withdrawable: number;
        currency: string;
    }>(
        transferFromAccountId && {
            url: API.TRADE.withdrawableCash(),
            params: {
                account_id: transferFromAccountId,
                // customer_id: security.currentCustomerId,
                business_type: 'FUND_TRANSACTION'
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    // 可输入的最大可转金额
    const maxAmount = useMemo(() => {
        if (typeof cashInfo?.withdrawable === 'number') {
            return Math.min(cashInfo.withdrawable, taxInfo.rest);
        }

        return undefined;
    }, [cashInfo, taxInfo.rest]);

    useEffect(() => {
        const firstIndividualAccountId = data.find(item => item.accountId && item.direction === 'OUT')?.accountId;

        if (transferFromAccountId || !firstIndividualAccountId) {
            return;
        }

        $setValues({
            from_account_id: firstIndividualAccountId
        });
    }, [transferFromAccountId, data, $setValues]);

    useEffect(() => {
        if (!isLoading) {
            const cachedParams = lscache.get(cacheKey);

            if (cachedParams) {
                $setValues(cachedParams);
                lscache.remove(cacheKey);
            }
        }
    }, [isLoading, cacheKey, $setValues]);

    const { outList, inList } = useMemo(() => {
        const outList: FieldSelectDataItemTypes[] = [];
        const inList: FieldSelectDataItemTypes[] = [];

        for (const item of data) {
            const listItem: FieldSelectDataItemTypes = {
                value: item.accountId,
                label: item.title
            };

            if (item.direction === 'OUT') {
                outList.push(listItem);
            }

            if (item.direction === 'IN') {
                inList.push(listItem);
            }
        }

        return { outList, inList };
    }, [data]);

    const handleViewRecords = () => {
        lscache.set(cacheKey, $params, 10);

        const revordsLocationState: LocationStateTypes = {
            from_account_id: $params.from_account_id,
            to_account_id: $params.to_account_id
        };

        navigate('../record', {
            state: revordsLocationState
        });
    };

    const handleSubmit = async () => {
        if ($invalid) {
            $batchDirty(true);
            Toast.show($getFirstError());
        } else {
            Toast.loading(true);

            try {
                await http.post(
                    API.TRADE.deposit.submitTransfer(),
                    {},
                    {
                        params: {
                            from_account_id: $params.from_account_id,
                            to_account_id: $params.to_account_id,
                            currency: $params.currency,
                            amount: $params.amount,
                            type: 'INDIVIDUAL_IRA',
                            tax_year: taxYear
                        }
                    }
                );

                // 综合账户入金成功之后税额会同步刷新，所以这里 cost 为 0，不需要在前端计算
                // 注：跳转过去的页面不会用到 limit
                const tax: TaxLimitInfo = { year: taxYear, cost: 0, limit: 0 };

                navigate('../complete', {
                    state: { tax }
                });

                Toast.loading(false);
            } catch (error) {
                Toast.loading(false);
                Toast.show(error);
            }
        }
    };

    const render = () => {
        if (error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (isLoading) {
            return <Loading />;
        }

        return (
            <Form layout="vertical" onFinish={handleSubmit}>
                <TaxLimit />
                <FormItem
                    line
                    required
                    label={__('Transfer From')}
                    name="from_account_id"
                    validMessage={{ required: __('Please select an Individual account') }}>
                    <FieldSelect renderInComponent data={outList} label={__('Transfer From')} />
                </FormItem>
                <FormItem
                    line
                    required
                    label={__('Transfer To')}
                    name="to_account_id"
                    $defaultValue={security.currentAccountId}
                    validMessage={{ required: __('Please select an IRA account') }}>
                    <FieldSelect renderInComponent data={inList} label={__('Transfer From')} />
                </FormItem>
                <div className="space-box">
                    <p className="label">{__('Contribution amount')}</p>
                    <CurrencyAmount
                        currencyList={currencyList}
                        selectConfig={{
                            name: 'currency',
                            disabled: true,
                            showIcon: false,
                            $defaultValue: 'USD'
                        }}
                        inputConfig={{
                            name: 'amount',
                            placeholder: '0.00',
                            checker: value => {
                                if (!value) {
                                    return __('Please enter an amount');
                                }

                                if (value <= 0) {
                                    return __('The amount you entered is invalid');
                                }

                                if (typeof maxAmount === 'number' && value > maxAmount) {
                                    return __('Please lower your contribution amount');
                                }

                                return true;
                            }
                        }}
                        extra={
                            <div className={style.help}>
                                {typeof maxAmount === 'number' && (
                                    <div>
                                        {__('Available to transfer:')} {numberal(maxAmount).format('0,0.[00]')} USD
                                    </div>
                                )}
                                <div>
                                    {printf(
                                        __('您在%s税年已存入%s，剩余额度%s'),
                                        taxYear,
                                        numberal(taxInfo.done).format('$0,0.00'),
                                        numberal(taxInfo.rest).format('$0,0.00')
                                    )}
                                </div>
                                <div>
                                    {__(
                                        'Inter-account transfers may not be settled immediately. You can check the status in "View Transfer Record".'
                                    )}
                                </div>
                            </div>
                        }
                    />
                </div>
                <BottomBox className="mt-5">
                    <Button
                        block
                        type="primary"
                        htmlType="submit"
                        className="btn-important"
                        loading={isLoading || taxLimitLoading}>
                        {__('Confirm')}
                    </Button>
                    <HashLink onClick={handleViewRecords} className={style.recordBtn}>
                        {__('View Transfer Records')}
                    </HashLink>
                </BottomBox>
            </Form>
        );
    };

    const getTitle = () => {
        switch (security.currentAccount!.customerType) {
            case AccountCustomerTypeEnum.IRA_ROTH:
                return __('Contribute to your Roth IRA via your Live Account');
            case AccountCustomerTypeEnum.IRA_TRADITIONAL:
                return __('Contribute to your Traditional IRA via your Live Account');
            case AccountCustomerTypeEnum.IRA_ROLLOVER:
                return __('Contribute to your Rollovewr IRA via your Live Account');
            default:
                return __('Internal Transfer');
        }
    };

    return (
        <div className={style.root}>
            <ScrollTop />
            <h3 className={style.title}>
                <span>{getTitle()}</span>
                <SwitchContributionWay currentWay="transfer" />
            </h3>
            <div>{render()}</div>
        </div>
    );
};

export default withForm(Fill);
